import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { REVIEW_CANCEL_QUERY_PARAM, isReviewInFlight } from "@reviewer73/shared";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { api } from "../api/client.js";
import { CancelReviewButton } from "../components/cancel-review-button.js";
import { EmptyNote } from "../components/empty-note.js";
import { Rack } from "../components/rack.js";
import { ReviewRow, ReviewRowSkeleton } from "../components/review-row.js";
import { ReviewStream } from "../components/review-stream.js";
import { SeverityBadge } from "../components/severity-badge.js";
import { StaggerBody, StaggerContainer, StaggerItem, StaggerRow } from "../components/stagger.js";
import { StatusBadge } from "../components/status-badge.js";
import { Strip, StripField, StripFields } from "../components/strip.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.js";
import { Table, TableCell, TableEmpty, TableHead, TableHeader, TableRow } from "../components/ui/table.js";
import { formatCost, formatDate, formatDay, formatDuration, formatTime, repoName } from "../lib/format.js";
import { useElapsedSeconds } from "../lib/use-elapsed-seconds.js";

/** Group reviews into [day label, reviews] pairs, newest day first. */
function groupByDay<T extends { createdAt: string }>(reviews: T[]): [string, T[]][] {
  const groups = new Map<string, T[]>();
  for (const review of reviews) {
    const day = formatDay(review.createdAt);
    const bucket = groups.get(day);
    if (bucket) bucket.push(review);
    else groups.set(day, [review]);
  }
  return [...groups.entries()];
}

export function Reviews() {
  const [state, setState] = useState("all");
  const { data, isLoading, error } = useQuery({
    queryKey: ["reviews", state],
    queryFn: () => api.reviews(state !== "all" ? { state } : {}),
  });

  if (error) {
    return (
      <div className="tile p-6 text-sm text-muted-foreground">
        Reviews konnten nicht geladen werden.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="flex items-baseline gap-2 text-xl font-bold tracking-tight">
          Reviews
          {!isLoading ? (
            <span className="text-sm font-normal tabular-nums text-muted-foreground">
              {data?.length ?? 0}
            </span>
          ) : null}
        </h1>
        <Select value={state} onValueChange={setState}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">alle</SelectItem>
            <SelectItem value="queued">wartet</SelectItem>
            <SelectItem value="running">läuft</SelectItem>
            <SelectItem value="done">fertig</SelectItem>
            <SelectItem value="failed">fehlgeschlagen</SelectItem>
            <SelectItem value="skipped">übersprungen</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <ReviewRowSkeleton count={6} />
      ) : !data || data.length === 0 ? (
        <EmptyNote>Kein Review mit diesem Status.</EmptyNote>
      ) : (
        // Search-result reading: grouped by day, each row a primary line plus
        // one quiet meta line — no repeated field labels.
        groupByDay(data ?? []).map(([day, reviews]) => (
          <Rack key={day} label={day} count={reviews.length} trayClassName="space-y-2">
            <StaggerContainer className="space-y-2">
              {reviews.map((review) => (
                <StaggerItem key={review.id}>
                  <ReviewRow review={review} time={formatTime(review.createdAt)} />
                </StaggerItem>
              ))}
            </StaggerContainer>
          </Rack>
        ))
      )}
    </div>
  );
}

const ARCHITECTURE_TOPIC: Record<string, string> = {
  approach: "Ansatz",
  architecture: "Architektur",
  simplification: "Vereinfachung",
};

export function ReviewDetail() {
  const { id = "" } = useParams();
  const [searchParams] = useSearchParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ["review", id],
    queryFn: () => api.review(id),
    // While the run is live, keep the head data fresh so it flips to done on its own.
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state && isReviewInFlight(state) ? 5000 : false;
    },
  });
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false });
  const isLive = data ? isReviewInFlight(data.state) : false;
  const elapsedS = useElapsedSeconds(data?.createdAt, isLive);

  if (error) {
    return (
      <div className="tile p-6 text-sm text-muted-foreground">
        Review konnte nicht geladen werden.
      </div>
    );
  }
  if (isLoading || !data) return <p className="p-4 text-sm text-muted-foreground">Review wird geladen…</p>;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/reviews" className="text-xs text-muted-foreground hover:text-foreground">
          ← Reviews
        </Link>
        <h1 className="mt-1 flex flex-wrap items-center gap-3 text-xl font-bold tracking-tight">
          {repoName(data.projectPath)} <span className="font-mono">!{data.mrIid}</span>
          <StatusBadge state={data.state} />
          <span className="ml-auto flex flex-wrap items-center gap-2">
            {isLive ? (
              <CancelReviewButton
                reviewId={id}
                initiallyConfirming={searchParams.has(REVIEW_CANCEL_QUERY_PARAM)}
              />
            ) : null}
            {me.data?.gitlabUrl ? (
              <Button asChild variant="outline" size="sm">
                <a
                  href={`${me.data.gitlabUrl.replace(/\/$/, "")}/${data.projectPath}/-/merge_requests/${data.mrIid}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Zum Merge Request
                  <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                </a>
              </Button>
            ) : null}
          </span>
        </h1>
      </div>

      {/* The strip itself, blown up: the review's printed head data. */}
      <Strip state={data.state}>
        <StripFields className="flex-wrap sm:flex-nowrap">
          <StripField label="Commit" className="min-w-28">
            <span className="font-mono text-xs">{data.reviewedSha.slice(0, 8)}</span>
          </StripField>
          {data.previousSha ? (
            <StripField label="Inkrementell seit" className="min-w-28">
              <span className="font-mono text-xs">{data.previousSha.slice(0, 8)}</span>
            </StripField>
          ) : null}
          <StripField label="Modell" className="min-w-44">
            <span className="font-mono text-xs">{data.model}</span>
          </StripField>
          {/* Only worth a field when it explains the cost — a single run is the norm. */}
          {data.mode === "fanout" ? (
            <StripField label="Fan-out" className="min-w-24">
              <span className="font-mono text-xs tabular-nums">
                {data.subagentCount === null ? "—" : `${data.subagentCount} Subagents`}
              </span>
            </StripField>
          ) : null}
          <StripField label="Dauer" className="min-w-20">
            <span className="font-mono text-xs tabular-nums">
              {elapsedS !== null ? formatDuration(elapsedS) : formatDuration(data.durationS)}
            </span>
          </StripField>
          <StripField label="Kosten" className="min-w-20">
            <span className="font-mono text-xs">{formatCost(data.costUsd)}</span>
          </StripField>
          <StripField label="Zeit" className="min-w-32">
            <span className="text-xs text-muted-foreground">{formatDate(data.createdAt)}</span>
          </StripField>
        </StripFields>
      </Strip>

      {data.error ? (
        <Rack label="Fehlgeschlagen">
          <pre className="tile overflow-x-auto whitespace-pre-wrap p-3 font-mono text-xs">
            {data.error}
          </pre>
        </Rack>
      ) : null}

      {/* Ahead of the findings: while the review runs this is the only section
          with anything in it at all. */}
      <Rack label={isLive ? "Läuft gerade" : "Verlauf"}>
        <ReviewStream reviewId={id} live={isLive} />
      </Rack>

      <Rack label="Findings" count={data.findings.length}>
        <div className="tile p-1">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Severity</TableHead>
                <TableHead>Regel</TableHead>
                <TableHead>Ort</TableHead>
                <TableHead>Meldung</TableHead>
                <TableHead>Zustellung</TableHead>
              </TableRow>
            </TableHeader>
            {data.findings.length === 0 ? (
              <TableEmpty colSpan={5}>Keine Findings — dieser Diff hat nichts ausgelöst.</TableEmpty>
            ) : (
              <StaggerBody>
                {data.findings.map((finding, index) => (
                  <StaggerRow key={`${finding.file}:${finding.line}:${index}`}>
                    <TableCell>
                      <SeverityBadge severity={finding.severity} />
                    </TableCell>
                    <TableCell className="font-mono">{finding.ruleId}</TableCell>
                    <TableCell className="font-mono">
                      {finding.file}:{finding.line}
                    </TableCell>
                    <TableCell>{finding.message}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {finding.delivered === "inline" ? "inline" : "Summary"}
                    </TableCell>
                  </StaggerRow>
                ))}
              </StaggerBody>
            )}
          </Table>
        </div>
      </Rack>

      <Rack label="Architektur & Ansatz" count={data.architectureNotes.length}>
        <div className="tile px-4 py-1">
          {data.architectureNotes.length === 0 ? (
            <EmptyNote>Keine Anmerkungen zum Ansatz. Ein leerer Abschnitt ist der Normalfall.</EmptyNote>
          ) : (
            // Hairline-separated rows, like the tables above — a Rack's tray
            // reads the same whether its rows are cells or prose.
            <ul className="divide-y divide-border text-sm">
              {data.architectureNotes.map((note, index) => (
                <li key={`${note.topic}:${index}`} className="py-3">
                  <Badge variant="secondary">{ARCHITECTURE_TOPIC[note.topic] ?? note.topic}</Badge>
                  <p className="mt-1 font-semibold">{note.title}</p>
                  <p className="whitespace-pre-line">{note.message}</p>
                  {note.files.length > 0 ? (
                    <p className="mt-1 font-mono text-xs text-muted-foreground">
                      {note.files.join(", ")}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Rack>

      <Rack label="Beobachtungen ohne Regel" count={data.observations.length}>
        <div className="tile px-4 py-1">
          {data.observations.length === 0 ? (
            <EmptyNote>Keine Beobachtungen für dieses Review.</EmptyNote>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {data.observations.map((observation, index) => (
                <li key={`${observation.file}:${observation.line}:${index}`} className="py-3">
                  <span className="font-mono text-xs text-muted-foreground">
                    {observation.file}:{observation.line}
                  </span>
                  <p>{observation.message}</p>
                  {observation.suggested_rule ? (
                    <Badge variant="secondary" className="mt-1">
                      Regelvorschlag: {observation.suggested_rule}
                    </Badge>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Rack>
    </div>
  );
}
